import {
  ActiveFollowedSecurityPolicyOptions,
  ActiveVertical,
} from '@amzn/aet-coachingform-kit/lib/types';
import { IntlShape } from 'react-intl';
import {
  BarRaiseChoices,
  ContactReviewItemLabel,
} from '@amzn/aet-contactrecommendation-kit';
import {
  AppSyncID,
  AWSDateTime,
  ContactInput,
  FollowedSecurityPolicyOptionsObject,
  VerticalObject,
  VerticalType,
  BarRaiseChoicesObject,
  ResolvedProblemOptionsObject,
  ControllableOptionsObject,
} from '../../../api/CoachingForm/schema';
import { ccxOldMetrics, getAllMetrics } from '../CoachingDetails/Data';
import { getObjectOptions } from '../FieldComponents/Helpers';
import { Option } from '../FieldComponents/Select';
import { getValuesByAttribute } from '@amzn/aet-coachingform-kit/lib/helper';
import { metricKeysByVertical } from '@amzn/aet-coachingform-kit/lib/metric';
import { ConsolidatedContact } from '../../../api/CoachingNotes/schema';
import { Profile } from '../../../Types';
import { checkGoUser } from '../../../Util/User';

export interface ContactFromGetter {
  contactID: AppSyncID;
  userID: AppSyncID;
  time: AWSDateTime;
  marketplaceID?: string;
  marketplaceDomainName?: string;
  contactInfo?: ContactInput;
  status?: string;
  calWeek?: string;
  calYear?: string;
  label?: string;
}

export const getAllContactID = (
  contactList: ConsolidatedContact[]
): Option[] => {
  return contactList.map(contact => {
    return { value: contact.contactID, label: contact.contactID };
  });
};

export const getContacts = (
  list: ContactFromGetter[] = [],
  reviewedLabel: string,
  recommendedLabel: string
): Option[] =>
  list
    .sort((a, b) => {
      // theoretically, value for field "label" returned from backend will only be 'REVIEWED', 'RECOMMENDED', 'RECENT'.
      // Since this field is optional, we also handle the "undefined" case here
      const aLabel = !a.label
        ? ContactReviewItemLabel.UNDEFINED
        : ContactReviewItemLabel[
            a.label! as keyof typeof ContactReviewItemLabel
          ];
      const bLabel = !b.label
        ? ContactReviewItemLabel.UNDEFINED
        : ContactReviewItemLabel[
            b.label! as keyof typeof ContactReviewItemLabel
          ];
      return aLabel - bLabel;
    })
    .map(({ contactID, label }) => {
      const extraLabel =
        label === ContactReviewItemLabel[ContactReviewItemLabel.REVIEWED]
          ? ` (${reviewedLabel})`
          : label === ContactReviewItemLabel[ContactReviewItemLabel.RECOMMENDED]
            ? ` (${recommendedLabel})`
            : '';
      return {
        value: contactID,
        label: `${contactID}${extraLabel}`,
      };
    });

export const getAllVerticals = (): Option[] => {
  const translations: Record<number, string> = {};

  return getObjectOptions(VerticalObject, translations);
};

export const getVerticals = (): Option[] => {
  return getAllVerticals().filter(vertical => vertical.value in ActiveVertical);
};

export const mapVertical = (
  vertical: VerticalType | undefined,
  profile: Profile | undefined
): VerticalType | undefined => {
  if (vertical === 'Consumer' || vertical === 'SDSCAP') {
    if (profile && !checkGoUser(profile.user?.owner)) {
      const mappedVertical = `${vertical}_V2` as VerticalType;
      return mappedVertical;
    }
  }
  return vertical;
};

export const getMetrics = (vertical?: VerticalType): Option[] => {
  // @TODO - Remove old CCX metrics and hiding logic  - SIM: https://sim.amazon.com/issues/CSKoala-1229
  // Hiding old CCX Metrics from the option list for backward compatible solution
  return getAllMetrics()
    .filter(
      option =>
        vertical === undefined ||
        vertical === null ||
        getValuesByAttribute(metricKeysByVertical, [vertical]).includes(
          option.value
        )
    )
    .filter(option => !ccxOldMetrics.includes(option.value.toString()))
    .sort((a, b) => {
      return a.label > b.label ? 1 : a.label < b.label ? -1 : 0;
    });
};

export const getAllSecurityOptions = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [FollowedSecurityPolicyOptionsObject.YES]: intl.formatMessage({
      id: 'cs_perfportal_yes',
      defaultMessage: 'Yes',
    }),
    [FollowedSecurityPolicyOptionsObject.NO]: intl.formatMessage({
      id: 'cs_perfportal_no',
      defaultMessage: 'No',
    }),
    [FollowedSecurityPolicyOptionsObject.NA]: intl.formatMessage({
      id: 'cs_perfportal_not_applicable_full',
      defaultMessage: 'Not Applicable',
    }),
  };

  return getObjectOptions(FollowedSecurityPolicyOptionsObject, translations);
};

export const getSecurityOptions = (intl: IntlShape): Option[] => {
  return getAllSecurityOptions(intl).filter(
    security => security.value in ActiveFollowedSecurityPolicyOptions
  );
};

export const getAllBarRaisedQuestionOptions = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [BarRaiseChoicesObject.RAISED_THE_BAR]: intl.formatMessage({
      id: 'cs_perfportal_contactReview_barRaisedQuestion_raisedTheBar',
      defaultMessage: 'Raised the bar',
    }),
    [BarRaiseChoicesObject.MET_THE_BAR]: intl.formatMessage({
      id: 'cs_perfportal_contactReview_barRaisedQuestion_metTheBar',
      defaultMessage: 'Met the bar',
    }),
    [BarRaiseChoicesObject.BELOW_THE_BAR]: intl.formatMessage({
      id: 'cs_perfportal_barRaisedQuestion_belowTheBar',
      defaultMessage: 'Below the bar',
    }),
    [BarRaiseChoicesObject.NOT_APPLICABLE]: intl.formatMessage({
      id: 'cs_perfportal_barRaisedQuestion_notApplicable',
      defaultMessage: 'Not applicable',
    }),
  };

  return getObjectOptions(BarRaiseChoicesObject, translations);
};

export const getBarRaisedQuestionOptions = (intl: IntlShape): Option[] => {
  return getAllBarRaisedQuestionOptions(intl).filter(
    barRaiseOption => barRaiseOption.value in BarRaiseChoices
  );
};

export const getAllResolvedProblemOptions = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [ResolvedProblemOptionsObject.YES]: intl.formatMessage({
      id: 'cs_perfportal_yes',
      defaultMessage: 'Yes',
    }),
    [ResolvedProblemOptionsObject.NO]: intl.formatMessage({
      id: 'cs_perfportal_no',
      defaultMessage: 'No',
    }),
  };
  return getObjectOptions(ResolvedProblemOptionsObject, translations);
};

export const getAllControllableOptions = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [ControllableOptionsObject.YES]: intl.formatMessage({
      id: 'cs_perfportal_yes',
      defaultMessage: 'Yes',
    }),
    [ControllableOptionsObject.NO]: intl.formatMessage({
      id: 'cs_perfportal_no',
      defaultMessage: 'No',
    }),
  };
  return getObjectOptions(ControllableOptionsObject, translations);
};
